// ---------------------------------------------------------------------------
// useAgentVersions — fetch the immutable version snapshots for a single agent.
// Stays idle (empty, non-loading) until both `workspaceName` and `agentName`
// are non-null. Returns versions in the order the server provides (ascending).
// Built on the shared `useAsyncData` primitive for race-safe loading + refresh.
// ---------------------------------------------------------------------------

import { useMemo } from 'react';

import { useHttpClient } from '../../api/HttpClientContext.js';
import { AgentsService } from '../../services/agentsService.js';
import type { AgentVersion } from '../../types/agent.js';
import { useAsyncData } from '../useAsyncData/useAsyncData.js';

import type {
  UseAgentVersionsParams,
  UseAgentVersionsResult,
} from './useAgentVersions.interface.js';

export function useAgentVersions(
  params: UseAgentVersionsParams,
): UseAgentVersionsResult {
  const { workspaceName, agentName } = params;
  const client = useHttpClient();
  const service = useMemo(() => new AgentsService(client), [client]);

  const fetcher = useMemo<
    ((signal: AbortSignal) => Promise<AgentVersion[]>) | null
  >(() => {
    if (workspaceName === null || agentName === null) return null;
    return (signal) =>
      service.listVersions(workspaceName, agentName, { signal });
  }, [service, workspaceName, agentName]);

  const { data, isLoading, error, refresh } = useAsyncData<AgentVersion[]>(
    fetcher,
    { errorMessage: 'Failed to load versions' },
  );

  return useMemo<UseAgentVersionsResult>(
    () => ({ versions: data ?? [], isLoading, error, refresh }),
    [data, isLoading, error, refresh],
  );
}
